import React, { useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, FormControl, MessageBlock } from '../components/ui';
import { formatCurrency, formatNumber, parseLocaleNumber } from '../utils/numberFormatter';
import { getCommercialSimulatorPolicy } from '../config/commercialSimulatorPolicy';
import {
  getSimulatorInstallmentOptions,
  getSimulatorProducts,
  quoteModalities,
  validateSimulationInput,
} from '../utils/commercialSimulator';
import '../styles/simulator.css';

function SimulatorPage() {
  const policy = getCommercialSimulatorPolicy();
  const products = useMemo(() => getSimulatorProducts(policy), [policy]);
  const installmentOptions = useMemo(
    () => getSimulatorInstallmentOptions(policy),
    [policy]
  );
  const emptyQuotes = useMemo(
    () =>
      Object.entries(policy?.modalities || {}).map(([id, modality]) => ({
        id,
        name: modality.name,
      })),
    [policy]
  );

  const [productId, setProductId] = useState(products[0]?.id || 'moto');
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [installments, setInstallments] = useState(12);

  const selectedProduct = products.find((product) => product.id === productId);
  const validation = validateSimulationInput({
    productId,
    amount,
    installments,
    policy,
  });
  const amountError = validation.errors.amount;
  const hasAmountInput = amount !== '' && amount !== null;

  const quoteResult = useMemo(
    () => quoteModalities({ productId, amount, installments, policy }),
    [productId, amount, installments, policy]
  );

  const handleAmountChange = (event) => {
    const value = event.target.value.replace(/[^0-9,]/g, '');

    if (value === '') {
      setAmount('');
      setDisplayAmount('');
      return;
    }

    const numericValue = parseLocaleNumber(value);
    if (!Number.isNaN(numericValue)) {
      setAmount(numericValue);
      setDisplayAmount(formatNumber(numericValue));
    }
  };

  return (
    <div className="page-content rg-simulator">
      <div className="page-title">
        <div className="row">
          <div className="col-12">
            <h3>Simulador</h3>
            <p className="text-muted mb-0">Cotizá cuotas sin identificar al cliente.</p>
          </div>
        </div>
      </div>

      <Card className="rg-simulator__form-card">
        <CardHeader>
          <h5 className="mb-0">Datos de la cotización</h5>
        </CardHeader>
        <CardBody>
          <div className="row g-3">
            <div className="col-md-4">
              <FormControl
                id="simulator-product"
                label="Producto"
                as="select"
                value={productId}
                onChange={(event) => setProductId(event.target.value)}
              >
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </FormControl>
            </div>

            <div className="col-md-4">
              <FormControl
                id="simulator-amount"
                label="Monto"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="0"
                value={displayAmount}
                onChange={handleAmountChange}
                status={amountError ? 'error' : 'default'}
                message={amountError || undefined}
                hint={
                  selectedProduct
                    ? `Máximo ${selectedProduct.name}: $${formatNumber(selectedProduct.maxAmount)}`
                    : undefined
                }
              />
            </div>

            <div className="col-md-4">
              <div className="rg-simulator__installments">
                <FormControl
                  id="simulator-installments"
                  label="Cuotas"
                  as="select"
                  value={installments}
                  onChange={(event) => setInstallments(Number(event.target.value))}
                  status={validation.errors.installments ? 'error' : 'default'}
                  message={validation.errors.installments || undefined}
                >
                  {installmentOptions.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </FormControl>
                <label className="rg-simulator__scroller-label" htmlFor="simulator-installments-scroller">
                  Ajustar cuotas
                </label>
                <input
                  id="simulator-installments-scroller"
                  className="rg-simulator__scroller rg-focusable"
                  type="range"
                  min={policy?.installments?.min}
                  max={policy?.installments?.max}
                  step="1"
                  value={installments}
                  onChange={(event) => setInstallments(Number(event.target.value))}
                  aria-valuemin={policy?.installments?.min}
                  aria-valuemax={policy?.installments?.max}
                  aria-valuenow={installments}
                />
                <div className="rg-simulator__scroller-scale">
                  <span>{policy?.installments?.min}</span>
                  <span>{installments}</span>
                  <span>{policy?.installments?.max}</span>
                </div>
              </div>
            </div>
          </div>

          {amountError && hasAmountInput && (
            <div className="rg-simulator__alert">
              <MessageBlock tone="error" title="Monto fuera de política">
                {amountError}
              </MessageBlock>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="row g-3 rg-simulator__results">
        {(quoteResult.ok ? quoteResult.quotes : emptyQuotes).map((quote) => (
          <div className="col-md-4" key={quote.id}>
            <Card className="rg-simulator__result-card">
              <CardBody>
                <h5 className="rg-simulator__result-title">{quote.name}</h5>
                <p className="rg-meta-label mb-1">Cuota</p>
                <p className="rg-simulator__quota rg-mono-amount">
                  {quote.installmentAmount
                    ? formatCurrency(quote.installmentAmount, 2)
                    : '—'}
                </p>
              </CardBody>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SimulatorPage;
